import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, Trash2, Wand2, ExternalLink, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Triage screen for Laaffic CDRs the backfill / live-webhook could not
 * ingest — because their taskId doesn't map to a campaign, or their
 * labelName has no canonical in the disposition map.
 *
 * Workflow:
 *   1. Filter by team + reason.
 *   2. Fix the underlying mapping (Campaigns page for taskId,
 *      Disposition mapping page for labels).
 *   3. Hit "Reprocess" — the backend retries brand + disposition
 *      resolution on every unresolved row and promotes the ones that
 *      now succeed into ops_call_rows.
 */
const REASON_LABELS = {
  unmapped_task: "Unmapped taskId",
  unmapped_disposition: "Unmapped disposition",
  missing_datetime: "Missing datetime / callId",
};

export default function LaafficUnmappedPage() {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [reason, setReason] = useState("");
  const [items, setItems] = useState([]);
  const [breakdown, setBreakdown] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (teamId) params.set("team_id", teamId);
      if (reason) params.set("reason", reason);
      const { data } = await api.get(`/admin/integrations/laaffic/unmapped?${params.toString()}`);
      setItems(data.items || []);
      setBreakdown(data.breakdown || {});
      setTotal(data.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [teamId, reason]);

  useEffect(() => { load(); }, [load]);

  const runReprocess = async () => {
    if (!teamId) {
      alert("Pick a team first — reprocess is scoped to one team at a time.");
      return;
    }
    setReprocessing(true);
    setReprocessResult(null);
    try {
      const params = new URLSearchParams({ team_id: teamId });
      if (reason) params.set("reason", reason);
      const { data } = await api.post(`/admin/integrations/laaffic/unmapped/reprocess?${params.toString()}`);
      setReprocessResult(data);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setReprocessing(false);
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this unmapped row? Skip this if you plan to reprocess later.")) return;
    try {
      await api.delete(`/admin/integrations/laaffic/unmapped/${id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="laaffic-unmapped-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">Laaffic unmapped queue</h1>
          <div className="text-sm text-slate-600 mt-1 max-w-2xl">
            Laaffic CDRs that ingest quarantined because their taskId or disposition label doesn&apos;t resolve.
            Fix the underlying mapping, then hit <b>Reprocess</b> to promote resolvable rows into ops_call_rows.
          </div>
          <div className="mt-2 flex gap-2 text-xs">
            <Link to="/campaigns" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Campaigns (map taskId)</Link>
            <Link to="/admin/laaffic-dispositions" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Disposition mapping</Link>
            <Link to="/admin/laaffic-backfill" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Backfill</Link>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="unmapped-reload">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <Button size="sm" onClick={runReprocess} disabled={reprocessing || !teamId} data-testid="unmapped-reprocess">
            {reprocessing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
            Reprocess {reason ? `(${REASON_LABELS[reason]})` : "all"}
          </Button>
        </div>
      </div>

      <div className="glass-card p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Team</label>
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)}
                  className="glass-input h-9 mt-1 min-w-[200px]" data-testid="unmapped-team">
            <option value="">— all teams —</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Reason</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
                  className="glass-input h-9 mt-1 min-w-[220px]" data-testid="unmapped-reason">
            <option value="">— all reasons —</option>
            {Object.keys(REASON_LABELS).map((r) => (
              <option key={r} value={r}>{REASON_LABELS[r]} ({breakdown[r] || 0})</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-slate-500 self-center pb-1">
          Showing <b className="tabular-nums">{items.length}</b> of <b className="tabular-nums">{total}</b> row{total === 1 ? "" : "s"}
        </div>
      </div>

      {err && (
        <div className="glass-card p-3 text-sm text-red-700 border-l-4 border-red-500" data-testid="unmapped-err">{err}</div>
      )}

      {reprocessResult && (
        <div className="glass-card p-3 border-l-4 border-emerald-500 text-sm text-emerald-900" data-testid="unmapped-reprocess-result">
          Reprocess done — <b>{reprocessResult.promoted}</b> promoted to ops_call_rows,{" "}
          <b>{reprocessResult.duplicate}</b> duplicate,{" "}
          <b>{reprocessResult.still_unmapped_task}</b> still unmapped (task),{" "}
          <b>{reprocessResult.still_unmapped_disposition}</b> still unmapped (disposition),{" "}
          <b>{reprocessResult.missing_datetime}</b> missing datetime.
        </div>
      )}

      <div className="glass-card overflow-hidden" data-testid="unmapped-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-slate-500 text-left">
              <th className="px-3 py-2">Attempt (IST)</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Customer / phone</th>
              <th className="px-3 py-2">Task ID</th>
              <th className="px-3 py-2">Label</th>
              <th className="px-3 py-2">Detail</th>
              <th className="px-3 py-2 text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                Nothing to triage. All CDRs in this window resolved cleanly.
              </td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 font-mono text-[10px] whitespace-nowrap">
                  {r.attempt_date_ist || "—"}
                  <div className="text-slate-400">{r.attempt_datetime ? String(r.attempt_datetime).slice(11, 19) : ""}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                    r.reason === "missing_datetime" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
                  }`}>
                    {REASON_LABELS[r.reason] || r.reason}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.customer_name || "—"}</div>
                  {r.phone_number && <div className="text-[10px] text-slate-500 font-mono">{r.phone_number}</div>}
                </td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.laaffic_task_id || "—"}</td>
                <td className="px-3 py-2 font-mono text-[10px] max-w-[160px] truncate" title={r.laaffic_label_raw || ""}>
                  {r.laaffic_label_raw || "—"}
                </td>
                <td className="px-3 py-2 text-[10px] text-slate-600 max-w-md">{r.detail || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1.5">
                    {r.laaffic_voice_url && (
                      <a href={r.laaffic_voice_url} target="_blank" rel="noreferrer" title="Play recording"
                         className="p-1 rounded hover:bg-slate-100"
                         data-testid={`unmapped-play-${r.id}`}>
                        <PlayCircle className="w-4 h-4 text-blue-600" />
                      </a>
                    )}
                    <Button size="sm" variant="outline" onClick={() => removeRow(r.id)}
                            data-testid={`unmapped-del-${r.id}`}>
                      <Trash2 className="w-3 h-3 text-red-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
